import { StyleSheet, css } from 'aphrodite'
import { useEffect, useRef, useState } from 'react'
import {
  DISPATCH_TEMPLATE_OPTIONS,
  PROJECT_TEMPLATE_OPTIONS,
  type ProjectTemplateRecipientType,
  type SenderStakeholderType,
  TAG_TEMPLATE_OPTIONS,
  type TagTemplateRecipientType,
  getSubHaulerTemplateLabel,
  getVisibleTemplateOptions,
} from '../../config/templateConfig'

interface TemplateSelectorProps {
  entityType: 'project' | 'dispatch' | 'tag'
  onSelect: (
    templateType: ProjectTemplateRecipientType | TagTemplateRecipientType,
  ) => void
  buttonLabel?: string
  // The current user's stakeholder type to filter visible templates
  userStakeholderType?: SenderStakeholderType
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  entityType,
  onSelect,
  buttonLabel,
  userStakeholderType,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const getOptions = () => {
    switch (entityType) {
      case 'project':
        // Filter project templates based on user's stakeholder type
        if (userStakeholderType) {
          const visibleOptions = getVisibleTemplateOptions(userStakeholderType)
          // Update labels for sub-hauler template based on sender type
          return visibleOptions.map((option) => ({
            ...option,
            label:
              option.value === 'SUB_HAULER'
                ? getSubHaulerTemplateLabel(userStakeholderType)
                : option.label,
          }))
        }
        return PROJECT_TEMPLATE_OPTIONS
      case 'dispatch':
        return DISPATCH_TEMPLATE_OPTIONS
      case 'tag':
        return TAG_TEMPLATE_OPTIONS
      default:
        return PROJECT_TEMPLATE_OPTIONS
    }
  }

  const getDefaultLabel = () => {
    switch (entityType) {
      case 'project':
        return 'New Project Terms'
      case 'dispatch':
        return 'New Dispatch'
      case 'tag':
        return 'New Tag'
      default:
        return 'New'
    }
  }

  const options = getOptions()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (
    value: ProjectTemplateRecipientType | TagTemplateRecipientType,
  ) => {
    onSelect(value)
    setIsOpen(false)
  }

  return (
    <div className={css(styles.container)} ref={dropdownRef}>
      <button
        className={css(styles.button)}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <i className="fa-solid fa-plus" />
        <span>{buttonLabel || getDefaultLabel()}</span>
        <i
          className={`fa-solid fa-chevron-down ${css(
            styles.chevron,
            isOpen && styles.chevronRotated,
          )}`}
        />
      </button>

      {isOpen && (
        <div className={css(styles.dropdown)}>
          <div className={css(styles.dropdownHeader)}>
            Templates for creating{' '}
            {entityType === 'project'
              ? 'Project Terms'
              : entityType === 'dispatch'
                ? 'Dispatches'
                : 'Tags'}
          </div>
          {options.map((option) => (
            <button
              key={option.value}
              className={css(styles.option)}
              onClick={() => handleSelect(option.value)}
              type="button"
            >
              <span className={css(styles.selectIndicator)}>[select]</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'inline-block',
  },
  button: {
    backgroundColor: '#1C64F2',
    color: '#000000',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '44px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    '@media (max-width: 1024px)': {
      padding: '4px 8px',
      fontSize: '10px',
      borderRadius: '6px',
      minHeight: '28px',
    },
  },
  chevron: {
    fontSize: '12px',
    transition: 'transform 0.2s ease',
  },
  chevronRotated: {
    transform: 'rotate(180deg)',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid #E5E7EB',
    minWidth: '300px',
    zIndex: 1000,
    overflow: 'hidden',
  },
  dropdownHeader: {
    backgroundColor: '#3B82F6',
    color: '#000000',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 600,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    textAlign: 'left',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  selectIndicator: {
    color: '#6B7280',
    fontSize: '12px',
  },
})

export default TemplateSelector
